import { Box, Typography } from "@mui/material";
import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Responsive, WidthProvider } from "react-grid-layout";
import { useTheme } from "@mui/material/styles";
import debounce from "lodash/debounce";
import { triggerWidgetEvent, updateWidgetData } from "./dashboard-service";
import { useWebSocket } from "../ws/websocket";
import { useToast } from "../common/toast";
import Button1Widget from "./widget/button-1";
import { prepareWidget } from "./widget/widget-util";
import { getDataWidgetComponent } from "./widget-registry";
import Title1Widget from "./widget/title-1";
import AutomationTitle1Widget from "./widget/automation-title1";
import { createGlobalStyle } from "styled-components";

const GlobalStyle = createGlobalStyle`
  .react-grid-item {
    transition: none !important;
    animation: none !important;
  }
  .react-grid-layout {
    transition: none !important;
    animation: none !important;
  }
  .react-grid-item > .react-resizable-handle {
    transition: none !important;
    animation: none !important;
  }
  .react-grid-item.react-grid-placeholder {
    transition: none !important;
    animation: none !important;
  }
  
  /* 禁用所有组件的动画效果 */
  .widget-container, 
  .widget-container *,
  .MuiBox-root,
  .MuiPaper-root,
  .MuiTypography-root,
  .MuiButton-root,
  .MuiSlider-root,
  .MuiTextField-root,
  .MuiInputBase-root,
  .MuiOutlinedInput-root,
  .MuiInputLabel-root,
  .MuiFormControl-root,
  .MuiSelect-root,
  .MuiMenuItem-root,
  .MuiCheckbox-root,
  .MuiRadio-root,
  .MuiSwitch-root,
  .MuiIconButton-root,
  .MuiSvgIcon-root {
    transition: none !important;
    animation: none !important;
  }
  
  .MuiButton-root:hover,
  .MuiIconButton-root:hover,
  .MuiSlider-thumb:hover,
  .MuiMenuItem-root:hover {
    transition: none !important;
    animation: none !important;
  }
`;

const ResponsiveGridLayout = WidthProvider(Responsive);

// Create a memoized widget component
const MemoizedWidget = React.memo(
  ({ widget, onValueUpdate, onStateUpdate, onTriggerEvent }) => {
    let DataWidgetComponent = null;
    switch (widget.skin) {
      case "default.title":
      case "default.title1":
        return <Title1Widget widget={widget} onValueUpdate={onValueUpdate} />;
      case "default.automationtitle":
        return (
          <AutomationTitle1Widget
            widget={widget}
            onStateUpdate={onStateUpdate}
          />
        );
      case "default.button":
        return <Button1Widget widget={widget} onValueUpdate={onTriggerEvent} />;
      default:
        DataWidgetComponent = getDataWidgetComponent(widget.skin);
        return (
          <DataWidgetComponent
            widget={widget}
            onValueUpdate={onValueUpdate}
            pattern={widget.pattern}
          />
        );
    }
  },
  (prevProps, nextProps) => {
    // Only compare if the widget object is the same reference
    return prevProps.widget === nextProps.widget;
  }
);

MemoizedWidget.displayName = "MemoizedWidget";

const DashboardCard = React.memo(
  ({
    card,
    drag = false,
    layoutSize = "sm",
    onLayoutChange,
    onStateUpdate,
    isEditing = false,
  }) => {
    const { ws, sendAndWaitWs, sendAndForgetWs } = useWebSocket();
    const { successToast, errorToast } = useToast();
    const [width, setWidth] = useState(1);
    const [layouts, setLayouts] = useState(null);
    const [showContent, setShowContent] = useState(false);

    // Prepare widgets once when card changes
    const preparedWidgets = useMemo(() => {
      return card.widgets.map((widget) => {
        prepareWidget(widget);
        return widget;
      });
    }, [card.widgets]);

    const handleLayoutChange = (layout) => {
      if (drag && onLayoutChange) {
        const updatedLayouts = layout.map((item) => ({
          id: item.i,
          layoutSize,
          layout: {
            x: item.x,
            y: item.y,
            w: item.w,
            h: item.h,
          },
        }));

        onLayoutChange(updatedLayouts);
      }
    };

    const handleUiWidgetValueChange = async (widgetItem) => {
      const widgetItems = Array.isArray(widgetItem) ? widgetItem : [widgetItem];
      const resp = await updateWidgetData(sendAndWaitWs, widgetItems);
      console.log("resp", resp);
      if (!resp) {
        errorToast("Error!");
      }
    };

    const handleTriggerWidgetEvent = useCallback(
      async (updateData) => {
        const resp = await triggerWidgetEvent(
          sendAndWaitWs,
          updateData.id,
          updateData.value
        );
        if (!resp) {
          errorToast("Error!");
        }
      },
      [sendAndWaitWs]
    );

    useEffect(() => {
      const w = Math.max(
        ...card.widgets.map((widget) => widget.layouts[layoutSize].w)
      );
      setWidth(w);
      const lo = card.widgets.map((w) => {
        return {
          i: w.id,
          x: w.layouts[layoutSize].x,
          y: w.layouts[layoutSize].y,
          w: w.layouts[layoutSize].w,
          h: w.layouts[layoutSize].h,
        };
      });
      setLayouts(lo);

      const timer = setTimeout(() => {
        setShowContent(true);
      }, 600);

      return () => clearTimeout(timer);
    }, [card]);

    // Memoize the rendered widgets
    const renderedWidgets = useMemo(() => {
      return preparedWidgets.map((widget) => (
        <Box
          key={widget.id}
          sx={{
            height: "100%",
            width: "100%",
            display: "flex",
            transition: "none !important",
            animation: "none !important",
          }}
        >
          {drag && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 20,
                zIndex: 10,
                backgroundColor: "transparent",
                cursor: "move",
                pointerEvents: "auto",
              }}
            />
          )}
          {drag && (
            <Box
              sx={{
                position: "absolute",
                left: 0,
                bottom: 0,
                width: "calc(100% - 20px)",
                height: 20,
                zIndex: 10,
                backgroundColor: "transparent",
                cursor: "move",
                pointerEvents: "auto",
              }}
            />
          )}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              width: "100%",
              padding: "2px",
              transition: "none !important",
              animation: "none !important",
              userSelect: drag || isEditing ? "none" : "auto",
              cursor: drag || isEditing ? "move" : "auto",
            }}
          >
            <MemoizedWidget
              widget={widget}
              onValueUpdate={handleUiWidgetValueChange}
              onStateUpdate={onStateUpdate}
              onTriggerEvent={handleTriggerWidgetEvent}
              isEditing={isEditing || drag}
              disableInteraction={drag || isEditing}
            />
          </Box>
        </Box>
      ));
    }, [
      preparedWidgets,
      handleUiWidgetValueChange,
      handleTriggerWidgetEvent,
      onStateUpdate,
      isEditing,
      drag,
    ]);

    return (
      <>
        <GlobalStyle />
        {layouts && (
          <ResponsiveGridLayout
            className="layout"
            layouts={{
              xs: layouts,
            }}
            breakpoints={{ xs: 0 }}
            cols={{ xs: width }}
            rowHeight={8}
            compactType={null}
            preventCollision={true}
            margin={[0, 0]}
            isDraggable={drag}
            isResizable={drag}
            onLayoutChange={handleLayoutChange}
            useCSSTransforms={false}
            transitionDuration={0}
            animate={false}
            autoSize={false}
            style={{
              userSelect: "none",
              WebkitUserSelect: "none",
              MozUserSelect: "none",
              msUserSelect: "none",
              opacity: showContent ? 1 : 0,
              transition: "opacity 0.1s ease-in-out",
            }}
          >
            {renderedWidgets}
          </ResponsiveGridLayout>
        )}
      </>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.card === nextProps.card &&
      prevProps.isEditTitle === nextProps.isEditTitle &&
      prevProps.drag === nextProps.drag &&
      prevProps.layoutSize === nextProps.layoutSize
    );
  }
);

export default DashboardCard;

DashboardCard.displayName = "DashboardCard";
